// WhatsApp + localStorage notification system (no Resend required)

export interface NotificationPayload {
  type: 'booking_confirmed' | 'lead_received' | 'abandoned_booking' | 'booking_recovery';
  name: string;
  phone?: string;
  email?: string;
  destination?: string;
  bookingRef?: string;
  pnr?: string;
  totalPrice?: number;
  message?: string;
}

const WHATSAPP_NUMBER = '442046152876'; // 020 4615 2876 in international format

export function buildWhatsAppMessage(payload: NotificationPayload): string {
  switch (payload.type) {
    case 'booking_confirmed':
      return encodeURIComponent(
        `✅ *Booking Confirmed - DIAL FOR FLIGHT*\n\n` +
        `Hello ${payload.name},\n\n` +
        `Your booking has been confirmed!\n\n` +
        `📋 *Booking Ref:* ${payload.bookingRef || 'N/A'}\n` +
        `🔑 *PNR:* ${payload.pnr || 'N/A'}\n` +
        `✈️ *Destination:* ${payload.destination || 'N/A'}\n` +
        `💷 *Total Paid:* £${payload.totalPrice?.toFixed(2) || '0.00'}\n\n` +
        `Our team will be in touch shortly to finalise your reservation.\n\n` +
        `📞 020 4615 2876 | 📧 info@dialforflight.co.uk`
      );
    case 'lead_received':
      return encodeURIComponent(
        `🔔 *New Enquiry - DIAL FOR FLIGHT*\n\n` +
        `Name: ${payload.name}\n` +
        `Phone: ${payload.phone || 'N/A'}\n` +
        `Email: ${payload.email || 'N/A'}\n` +
        `Destination: ${payload.destination || 'N/A'}\n\n` +
        `${payload.message || ''}\n\n` +
        `Reply to this message to respond to the enquiry.`
      );
    case 'abandoned_booking':
      return encodeURIComponent(
        `⏰ *Complete Your Booking - DIAL FOR FLIGHT*\n\n` +
        `Hello ${payload.name},\n\n` +
        `You left a booking incomplete for *${payload.destination || 'your trip'}*.\n\n` +
        `Don't miss out! Complete your booking now or call us:\n` +
        `📞 020 4615 2876\n` +
        `💬 WhatsApp: 020 4615 2876\n\n` +
        `We'll hold your details for 24 hours.`
      );
    case 'booking_recovery':
      return encodeURIComponent(
        `🔄 *Resume Your Booking - DIAL FOR FLIGHT*\n\n` +
        `Hello ${payload.name},\n\n` +
        `We saved your booking details for *${payload.destination || 'your trip'}*.\n\n` +
        `Click the link to complete your booking or call us:\n` +
        `📞 020 4615 2876`
      );
    default:
      return encodeURIComponent(`Hello from DIAL FOR FLIGHT! 📞 020 4615 2876`);
  }
}

export function openWhatsAppNotification(payload: NotificationPayload): void {
  const message = buildWhatsAppMessage(payload);
  const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
  if (typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

// localStorage notification log
export interface StoredNotification {
  id: string;
  type: NotificationPayload['type'];
  name: string;
  destination?: string;
  bookingRef?: string;
  timestamp: string;
  read: boolean;
}

export function saveNotificationToStorage(payload: NotificationPayload): void {
  if (typeof window === 'undefined') return;
  const notifications: StoredNotification[] = JSON.parse(
    localStorage.getItem('dff_notifications') || '[]'
  );
  notifications.unshift({
    id: Date.now().toString(),
    type: payload.type,
    name: payload.name,
    destination: payload.destination,
    bookingRef: payload.bookingRef,
    timestamp: new Date().toISOString(),
    read: false,
  });
  // Keep last 50
  localStorage.setItem('dff_notifications', JSON.stringify(notifications.slice(0, 50)));
}

export function getStoredNotifications(): StoredNotification[] {
  if (typeof window === 'undefined') return [];
  return JSON.parse(localStorage.getItem('dff_notifications') || '[]');
}

export function markNotificationsRead(): void {
  if (typeof window === 'undefined') return;
  const notifications = getStoredNotifications().map(n => ({ ...n, read: true }));
  localStorage.setItem('dff_notifications', JSON.stringify(notifications));
}
